import { useFormikContext } from 'formik';
import { ChangeEvent, useState } from 'react';
import { Plus } from 'lucide-react';

import { ButtonSelector } from '@@/form-components/ButtonSelector/ButtonSelector';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Button } from '@@/buttons';
import { Input } from '@@/form-components/Input';
import { InputList } from '@@/form-components/InputList';
import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { BoxSelector } from '@@/BoxSelector';
import { Modal } from '@@/modals';
import { FormError } from '@@/form-components/FormError';

import {
  buildLabelsItem,
  ExpressionOperatorOptions,
  ExpressionOperatorType,
  MatchLabel,
  KubernetesNetworkSecurityFormValues,
  NetworkSecurityRule,
  PodSelectionOptions,
  PodSelectionType,
  Protocol,
  ProtocolOptions,
  IngressRulePresetOptions,
  RulePresetType,
  SourceTypeOptions,
  TrafficSourceType,
  EgressRulePresetOptions,
} from './types';
import { MatchLabelsItem } from './MatchLabelsItem';

type Props = {
  ruleKind: 'ingress' | 'egress';
  isOpen: boolean;
  onClose: () => void;
};

export function CreateNetworkSecurityRuleModal({
  ruleKind,
  isOpen,
  onClose,
}: Props) {
  const {
    values: { ingressRules, egressRules },
    setFieldValue,
  } = useFormikContext<KubernetesNetworkSecurityFormValues>();

  const upperKind = ruleKind.charAt(0).toUpperCase() + ruleKind.slice(1);
  const title = 'Add ' + upperKind + ' Rule';
  const subHeading =
    (ruleKind === 'ingress' ? 'Inbound' : 'Outbound') + ' Traffic Rule';
  const [preset, setPreset] = useState<RulePresetType>(RulePresetType.CUSTOM);
  const [presetSelected, setPresetSelected] = useState<boolean>(false);
  const [tempRuleName, setTempRuleName] = useState<string>('');
  const [tempRuleAppliesType, setTempRuleAppliesType] =
    useState<PodSelectionType>();
  const [tempRuleAppliesLabels, setTempRuleAppliesLabels] = useState<
    MatchLabel[]
  >([]);
  const [tempRuleAppliesExpressionKey, setTempRuleAppliesExpressionKey] =
    useState<string>('');
  const [
    tempRuleAppliesExpressionOperator,
    setTempRuleAppliesExpressionOperator,
  ] = useState<ExpressionOperatorType>(ExpressionOperatorType.In);
  const [tempRuleAppliesExpressionValues, setTempRuleAppliesExpressionValues] =
    useState<string[]>([]);
  const [tempRuleSourceType, setTempRuleSourceType] =
    useState<TrafficSourceType>();
  const [tempRuleNamespace, setTempRuleNamespace] = useState<string>('');
  const [tempRuleCIDR, setTempRuleCIDR] = useState<string>('');
  const [tempRulePort, setTempRulePort] = useState<number | undefined>(
    undefined
  );
  const [tempRuleProtocol, setTempRuleProtocol] = useState<Protocol>('');
  const [tempRulePodLabelKey, setTempRulePodLabelKey] = useState<string>('');
  const [tempRulePodLabelValue, setTempRulePodLabelValue] =
    useState<string>('');

  const showNamespace =
    tempRuleSourceType === TrafficSourceType.Namespace ||
    tempRuleSourceType === TrafficSourceType.NamespacePodLabel;

  const showPodLabel =
    tempRuleSourceType === TrafficSourceType.NamespacePodLabel ||
    tempRuleSourceType === TrafficSourceType.PodLabelOnly;

  const namePlaceholder = 'e.g. Deny all ' + ruleKind;

  if (!isOpen) {
    return null;
  }

  const presetTypes =
    ruleKind === 'ingress' ? IngressRulePresetOptions : EgressRulePresetOptions;

  return (
    <Modal onDismiss={handleClosedModal} size="lg" aria-label={title}>
      <Modal.Header title={<h1 className="m-0 ml-1 text-base">{title}</h1>} />
      <form onSubmit={(e) => e.preventDefault()} data-cy="add-rule-form">
        {!presetSelected ? (
          <>
            <BoxSelector
              radioName="rulePreset"
              options={presetTypes}
              value={preset}
              onChange={handleChangePreset}
              useGridLayout
            />
            <div className="form-group">
              <div className="col-sm-12 flex gap-2">
                <Button
                  color="primary"
                  size="small"
                  onClick={handlePresetSelect}
                  data-cy="add-network-security-rule-preset-continue-button"
                  className="!ml-0"
                >
                  Continue
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {typeof canAddRule() === 'string' && (
              <FormError>{canAddRule()}</FormError>
            )}
            <FormControl
              label="Name"
              tooltip="Names must consist of lowercase alphanumeric characters or hyphens, and must start and end with an alphanumeric character"
              required
            >
              <Input
                value={tempRuleName}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setTempRuleName(e.target.value)
                }
                placeholder={namePlaceholder}
                data-cy="kubernetes-network-security-rule-name-input"
              />
            </FormControl>
            <FormControl label="Pod Selection" required>
              <PortainerSelect
                value={tempRuleAppliesType}
                onChange={(selectedOption: PodSelectionType) =>
                  setTempRuleAppliesType(selectedOption)
                }
                options={PodSelectionOptions}
                data-cy="kubernetes-network-security-rule-pod-selection-input"
              />
            </FormControl>
            {tempRuleAppliesType === PodSelectionType.MatchLabels && (
              <div className="flex items-center gap-4">
                <InputList<MatchLabel>
                  addLabel="Add matching label"
                  value={tempRuleAppliesLabels}
                  onChange={(selectedOption: MatchLabel[]) =>
                    setTempRuleAppliesLabels(selectedOption)
                  }
                  item={MatchLabelsItem}
                  itemBuilder={buildLabelsItem}
                  data-cy="kubernetes-network-security-rule-pod-selection-labels-input"
                />
              </div>
            )}
            {tempRuleAppliesType === PodSelectionType.MatchExpression && (
              <>
                <FormControl label="Key" required>
                  <Input
                    value={tempRuleAppliesExpressionKey}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setTempRuleAppliesExpressionKey(e.target.value)
                    }
                    placeholder="e.g. portainer.io/tier"
                    data-cy="kubernetes-network-security-rule-pod-selection-expression-key-input"
                  />
                </FormControl>
                <FormControl label="Operator" required>
                  <PortainerSelect
                    value={tempRuleAppliesExpressionOperator}
                    onChange={(selectedOption: ExpressionOperatorType) =>
                      setTempRuleAppliesExpressionOperator(selectedOption)
                    }
                    options={ExpressionOperatorOptions}
                    data-cy="kubernetes-network-security-rule-pod-selection-expression-operator-input"
                  />
                </FormControl>
                <FormControl label="Values" required>
                  <Input
                    value={tempRuleAppliesExpressionValues.join(', ')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setTempRuleAppliesExpressionValues(
                        e.target.value
                          .split(',')
                          .map((v) => v.trim())
                          .filter(Boolean)
                      )
                    }
                    placeholder="frontend, backend"
                    data-cy="kubernetes-network-security-rule-pod-selection-expression-values-input"
                  />
                </FormControl>
              </>
            )}
            <FormSection title={subHeading}>
              <FormControl label="Source Type" required>
                <PortainerSelect
                  value={tempRuleSourceType}
                  onChange={(selectedOption: TrafficSourceType) =>
                    setTempRuleSourceType(selectedOption)
                  }
                  options={SourceTypeOptions}
                  data-cy="kubernetes-network-security-rule-source-type-input"
                />
              </FormControl>
              {showNamespace && (
                <FormControl label="Namespace" required>
                  <Input
                    value={tempRuleNamespace}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setTempRuleNamespace(e.target.value)
                    }
                    placeholder="Custom"
                    data-cy="kubernetes-network-security-rule-namespace-input"
                  />
                </FormControl>
              )}
              {showPodLabel && (
                <FormControl label="Pod label">
                  <div className="flex items-center gap-2">
                    <Input
                      value={tempRulePodLabelKey}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setTempRulePodLabelKey(e.target.value)
                      }
                      className="w-1/2"
                      placeholder="key"
                      data-cy="kubernetes-network-security-rule-pod-key-input"
                    />
                    <Input
                      value={tempRulePodLabelValue}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setTempRulePodLabelValue(e.target.value)
                      }
                      className="w-1/2"
                      placeholder="value"
                      data-cy="kubernetes-network-security-rule-pod-value-input"
                    />
                  </div>
                </FormControl>
              )}
              {tempRuleSourceType === TrafficSourceType.CIDRBlock && (
                <FormControl label="CIDR">
                  <Input
                    value={tempRuleCIDR}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setTempRuleCIDR(e.target.value)
                    }
                    placeholder="e.g. 10.0.0.0/8"
                    data-cy="kubernetes-network-security-rule-cidr-input"
                  />
                </FormControl>
              )}
              <FormControl label="Port">
                <div className="flex items-center gap-2">
                  <Input
                    name="rulePort"
                    value={tempRulePort ?? ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const port =
                        e.target.value === ''
                          ? undefined
                          : Number(e.target.value);
                      setTempRulePort(port);
                    }}
                    className="w-1/2"
                    min={1}
                    max={65535}
                    placeholder="e.g. 9090 (blank = any)"
                    data-cy="kubernetes-network-security-rule-port-input"
                  />
                  <ButtonSelector<Protocol>
                    value={tempRuleProtocol}
                    options={ProtocolOptions}
                    onChange={(selectedOption: Protocol) =>
                      setTempRuleProtocol(selectedOption)
                    }
                    data-cy="kubernetes-network-security-rule-protocol-input"
                  />
                </div>
              </FormControl>
            </FormSection>

            <div className="form-group">
              <div className="col-sm-12 flex gap-2">
                <Button
                  icon={Plus}
                  disabled={typeof canAddRule() === 'string'}
                  color="primary"
                  size="small"
                  onClick={handleAddRule}
                  data-cy="add-network-security-add-rule-button"
                  className="!ml-0"
                >
                  Add Rule
                </Button>
              </div>
            </div>
          </>
        )}
      </form>
    </Modal>
  );

  function handleClosedModal() {
    setPreset(RulePresetType.CUSTOM);
    setPresetSelected(false);
    setTempRuleName('');
    setTempRuleAppliesType(undefined);
    setTempRuleAppliesLabels([]);
    setTempRuleAppliesExpressionKey('');
    setTempRuleAppliesExpressionOperator(ExpressionOperatorType.In);
    setTempRuleAppliesExpressionValues([]);
    setTempRuleSourceType(undefined);
    setTempRuleNamespace('');
    setTempRuleCIDR('');
    setTempRulePort(undefined);
    setTempRuleProtocol('');
    setTempRulePodLabelKey('');
    setTempRulePodLabelValue('');
    onClose();
  }

  function handleChangePreset(preset: RulePresetType) {
    setPreset(preset);
  }

  function handlePresetSelect() {
    switch (preset) {
      case RulePresetType.INGRESS_BLOCK_ALL_INBOUND:
        setTempRuleName('deny-all');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.DenyAll);
        break;
      case RulePresetType.INGRESS_ALLOW_ALL_INBOUND:
        setTempRuleName('allow-all');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.Any);
        break;
      case RulePresetType.INGRESS_ALLOW_INGRESS_CONTROLLER:
        setTempRuleName('allow-ingress-controller');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.NamespacePodLabel);
        setTempRuleNamespace('ingress-nginx');
        setTempRulePodLabelKey('app.kubernetes.io/name');
        setTempRulePodLabelValue('ingress-nginx');
        break;
      case RulePresetType.INGRESS_ALLOW_FROM_NAMESPACE:
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.Namespace);
        break;
      case RulePresetType.INGRESS_ALLOW_PROMETHEUS:
        setTempRuleName('allow-prometheus');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.NamespacePodLabel);
        setTempRuleNamespace('monitoring');
        setTempRulePodLabelKey('app');
        setTempRulePodLabelValue('prometheus');
        break;
      case RulePresetType.EGRESS_BLOCK_ALL_OUTBOUND:
        setTempRuleName('deny-all');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.DenyAll);
        break;
      case RulePresetType.EGRESS_ALLOW_ALL_OUTBOUND:
        setTempRuleName('allow-all');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.Any);
        break;
      case RulePresetType.EGRESS_ALLOW_DNS:
        setTempRuleName('allow-dns');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.NamespacePodLabel);
        setTempRuleNamespace('kube-system');
        setTempRulePodLabelKey('k8s-app');
        setTempRulePodLabelValue('kube-dns');
        setTempRulePort(53);
        break;
      case RulePresetType.EGRESS_ALLOW_NAMESPACE:
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.Namespace);
        break;
      case RulePresetType.EGRESS_ALLOW_HTTPS:
        setTempRuleName('allow-https');
        setTempRuleAppliesType(PodSelectionType.All);
        setTempRuleSourceType(TrafficSourceType.Any);
        setTempRulePort(443);
        setTempRuleProtocol('TCP');
        break;
      case RulePresetType.CUSTOM:
        break;
    }
    setPresetSelected(true);
  }

  function canAddRule() {
    if (!tempRuleName) return 'Name is required';

    // Names must consist of lowercase alphanumeric characters or hyphens, and
    // must start and end with an alphanumeric character.
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(tempRuleName)) {
      return 'Names must consist of lowercase alphanumeric characters or hyphens, and must start and end with an alphanumeric character.';
    }

    for (const rule of ingressRules) {
      if (rule.name === tempRuleName) {
        return 'Names of rules cannot contain duplicates';
      }
    }
    for (const rule of egressRules) {
      if (rule.name === tempRuleName) {
        return 'Names of rules cannot contain duplicates';
      }
    }

    if (!tempRuleAppliesType) return 'Pod Selection is required';
    if (!tempRuleSourceType) return 'Source Type is required';

    return undefined;
  }

  function handleAddRule() {
    if (typeof canAddRule() === 'string') return;

    const appliesLabels =
      tempRuleAppliesType === PodSelectionType.MatchLabels
        ? Object.fromEntries(
            tempRuleAppliesLabels.map((label) => [label.key, label.value])
          )
        : undefined;

    const appliesExpressions =
      tempRuleAppliesType === PodSelectionType.MatchExpression
        ? [
            {
              key: tempRuleAppliesExpressionKey,
              operator: tempRuleAppliesExpressionOperator,
              values: tempRuleAppliesExpressionValues,
            },
          ]
        : undefined;

    const podLabels = showPodLabel
      ? {
          [tempRulePodLabelKey]: tempRulePodLabelValue,
        }
      : undefined;

    const rule: NetworkSecurityRule = {
      name: tempRuleName,
      appliesTo: {
        type: tempRuleAppliesType!,
        matchLabels: appliesLabels,
        matchExpressions: appliesExpressions,
      },
      sourceType: tempRuleSourceType,
      namespace: tempRuleNamespace,
      podLabels: podLabels,
      cidr: tempRuleCIDR,
      port: tempRulePort,
      protocol: tempRuleProtocol,
    };
    if (ruleKind === 'ingress') {
      setFieldValue('ingressRules', [...ingressRules, rule]);
    } else {
      setFieldValue('egressRules', [...egressRules, rule]);
    }
    handleClosedModal();
  }
}
